import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Policy, PolicyActionModel } from '../../../types';
import {
  UserKeys,
  getAllGPanelRoles,
  createPolicy,
  editPolicy,
  clonePolicy,
  getAllPoliciesQuery,
  getDomainsQuery,
} from '../../api/users-api';
import { useAnalytics } from '../../shared/analytics/create-events';
import { useStore } from '../state/zustand-store';
import { ModuleList } from '../../shared/analytics/module-list';
import { handleErrors } from '../../utils/errors';
import * as analytics from '../../shared/analytics/analytics.json';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../utils/roles/client-roles';

import ChangeLogDialog from '../../shared/change-log-dialog';
import StepFour from './step-four';
import StepOne from './step-one';
import StepSeven from './step-seven';
import StepTwo from './step-two';
import { useUserSettings } from '../../utils/use-user-settings';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SimpleBreadcrumbsPage from '../../shared/simple-breadcrumbs-page';
import SimpleFormPage from '../../shared/simple-form-page';
import { pagesConfig } from '../../shared/pages-config';
import SharedFormStepper, {
  FormSubSteps,
  StepConfig,
} from '../../shared/shared-form-stepper';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../shared/unsaved-changes-dialog';

import {
  ActionSchema,
  BasicInformationSchema,
  PolicyFormSchema,
  ScheduleSchema,
  TargetsSchema,
  type PolicyAction,
} from './policy-schema';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import FormSkeleton from './form-skeleton';
import { policyActions } from '../../shared/policy-actions';
import SharedScheduleStep from '../../shared/shared-schedule-step';
import { transformActionHash } from './policy-utils';
import { Messages } from '../../utils/message-constants';
import Box from '@mui/material/Box';

export type { PolicyAction } from './policy-schema';
export type PolicyFormValues = z.infer<typeof PolicyFormSchema>;

// Utility type to create the dynamic field names
export type PolicyFormField =
  | keyof PolicyForm
  | `actionData.${number}.${keyof PolicyAction}`;

export interface DynamicGroupAssignmentModel {
  profileFieldType?: string;
  profileField?: string;
  regEx?: string;
  exact?: boolean;
  groupEmail?: string[];
  groupRole?: string[];
}

export interface PolicyForm extends Partial<Policy> {
  actionData: PolicyAction[];
}

export default function PolicyFormPage() {
  const { createEvent } = useAnalytics();
  const { id } = useParams();
  const selectedRowId = Number(id);
  const location = useLocation();
  const pathname = location.pathname;
  const resumeRuleId = (location.state as { resumeRuleId?: number } | null)
    ?.resumeRuleId;
  const dialogType = pathname.includes(pagesConfig.createPolicy.link)
    ? 'create'
    : pathname.includes(pagesConfig.editPolicy.link.replace('/:id', ''))
    ? 'edit'
    : 'clone';
  const queryClient = useQueryClient();
  const { data: policies, isLoading: isPolicyLoading } = useQuery(
    getAllPoliciesQuery()
  );
  const { data: domains, isLoading: isDomainLoading } = useQuery(
    getDomainsQuery()
  );
  const selectedPolicy = policies?.filter((p) => p.id === selectedRowId)[0];
  const { loggedInUser, directoryRoles, changeLogRoles } = useUserSettings();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [stepCompleted, setStepCompleted] = useState<boolean[]>(
    dialogType !== 'edit'
      ? [false, false, false, false, false, false]
      : [true, true, true, true, true, true]
  );
  const [activeStep, setActiveStep] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [actionSubSteps, setActionSubSteps] = useState<FormSubSteps[]>([]);
  const firstIdWithError =
    policyActions.find(
      (o) => actionSubSteps.find((step) => step.hasError)?.title == o.label
    )?.value || '';

  const navigate = useNavigate();

  const handleCancel = () => {
    navigate(`/${pagesConfig.policies.link}`, {
      state: { fromCancelButton: true },
    });
  };

  let steps: StepConfig[] = [
    { label: 'Basic Information', schema: BasicInformationSchema },
    { label: 'Actions', schema: ActionSchema },
    { label: 'Targets', schema: TargetsSchema },
    { label: 'Review' },
  ];

  const { data: gPanelRoles } = useQuery({
    queryKey: [UserKeys.gPanelRoles],
    queryFn: () => getAllGPanelRoles(),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty },
    trigger,
    getValues,
    setValue,
    watch,
    clearErrors,
    formState,
  } = useForm<PolicyFormValues>({
    resolver: zodResolver(PolicyFormSchema),
    mode: 'onChange',
    values:
      dialogType === 'edit' && selectedPolicy
        ? {
            ...selectedPolicy,
            lastRunDate: selectedPolicy.lastRunDate
              ? selectedPolicy.lastRunDate
              : 0,
            actionData: selectedPolicy.actionHash
              ? transformActionHash(selectedPolicy.actionHash)
              : [],
            targetsExcluded: selectedPolicy.targetsExcluded
              ? selectedPolicy.targetsExcluded
              : [],
          }
        : dialogType === 'clone' && selectedPolicy
        ? {
            ...selectedPolicy,
            id: undefined,
            lastRunDate: 0,
            createdBy: loggedInUser,
            policyName: selectedPolicy.policyName + '_Clone',
            actionData: selectedPolicy.actionHash
              ? transformActionHash(selectedPolicy.actionHash)
              : [],
            targetsExcluded: selectedPolicy.targetsExcluded
              ? selectedPolicy.targetsExcluded
              : [],
          }
        : {
            createdBy: loggedInUser,
            lastRunDate: 0,
            policyName: '',
            description: '',
            manualRun: true,
            scheduled: false,
            useRunDate: false,
            runDate: 0,
            sourceType: 'User' as const,
            policyType: 'standard' as const,
            actions: [],
            sources: [],
            runImmediately: false,
            allSource: false,
            actionData: [],
            emailsToNotify: [],
            emailsToShare: [],
            daysOfWeek: false,
            notifyViaEmail: false,
            dates: [],
            times: [],
            targetsExcluded: [],
          },
  });

  const scheduled = watch('scheduled');
  const policyType = watch('policyType') || '';
  const actions = watch('actions') || [];

  if (
    policyType === 'standard' &&
    scheduled &&
    actions.includes('Create Gmail Filter') &&
    actions.includes('Set Profile Information')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Targets', schema: TargetsSchema },
      { label: 'Schedule', schema: ScheduleSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'standard' &&
    scheduled &&
    actions.includes('Create Gmail Filter')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Targets', schema: TargetsSchema },
      { label: 'Schedule', schema: ScheduleSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'standard' &&
    scheduled &&
    actions.includes('Set Profile Information')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Targets', schema: TargetsSchema },
      { label: 'Schedule', schema: ScheduleSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'standard' &&
    !scheduled &&
    actions.includes('Create Gmail Filter') &&
    actions.includes('Set Profile Information')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Targets', schema: TargetsSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'standard' &&
    !scheduled &&
    actions.includes('Create Gmail Filter')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Targets', schema: TargetsSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'standard' &&
    !scheduled &&
    actions.includes('Set Profile Information')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Targets', schema: TargetsSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'newUser' &&
    actions.includes('Create Gmail Filter') &&
    actions.includes('Set Profile Information')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'newUser' &&
    actions.includes('Create Gmail Filter')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'newUser' &&
    actions.includes('Set Profile Information')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'newUser' &&
    !actions.includes('Create Gmail Filter') &&
    !actions.includes('Set Profile Information')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'standard' &&
    scheduled &&
    !actions.includes('Create Gmail Filter')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Targets', schema: TargetsSchema },
      { label: 'Schedule', schema: ScheduleSchema },
      { label: 'Review' },
    ];
  } else if (
    policyType === 'newUser' &&
    !actions.includes('Create Gmail Filter')
  ) {
    steps = [
      { label: 'Basic Information', schema: BasicInformationSchema },
      { label: 'Actions', schema: ActionSchema },
      { label: 'Review' },
    ];
  }

  const { mutate: mutateCreatePolicy, isPending: isCreatingPolicy } =
    useMutation({
      mutationFn: (data: Partial<Policy>) =>
        createPolicy(data, changeLogMessage),
      onSuccess: (returnData, data) => {
        const newPolicy = {
          ...(data as Policy),
          id: returnData,
        };

        queryClient.setQueryData<Policy[] | undefined>(
          [UserKeys.Policies],
          (oldData) => {
            if (!oldData) return;
            return [...oldData, newPolicy];
          }
        );

        if (resumeRuleId) {
          const resumeUrl = `/${pagesConfig.rulesEngine.link}/${pagesConfig.editRule.link}/${resumeRuleId}`;
          bypass();
          navigate(`/${pagesConfig.policies.link}`);
          setSnackbar({
            open: true,
            severity: 'success',
            message: Messages.Success.Created('Policy'),
            action: {
              label: 'RESUME RULE CREATION',
              onClick: () => {
                setSnackbar({ open: false, severity: 'success', message: '' });
                navigate(resumeUrl, {
                  state: { autoSelectPolicyId: returnData },
                });
              },
            },
          });
        } else {
          setSnackbar({
            open: true,
            severity: 'success',
            message: Messages.Success.Added('Policy'),
          });
          bypass();
          navigate(`/${pagesConfig.policies.link}`);
        }
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue adding this policy. Please refresh and try again.',
        });
      },
      onSettled: (_returnData, _error, data) => {
        createEvent(analytics.automation.policies.create_policy, {
          module: ModuleList.modules.automation.name,
          section: ModuleList.modules.automation.sections.policies,
          policyType: data.policyType,
          manualRun: data.manualRun,
          targetType: data.sourceType,
          scheduled: data.scheduled,
          actions: data.actions,
          runImmediately: data.runImmediately,
        });
        setOpenChangeLogDialog(false);
      },
    });

  const { mutate: mutateEditPolicy, isPending: isEditingPolicy } = useMutation({
    mutationFn: (data: Partial<Policy>) => editPolicy(data, changeLogMessage),
    onSuccess: (returnData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Policy updated successfully!',
      });

      const newPolicy = {
        ...(data as Policy),
        id: returnData,
      };

      queryClient.setQueryData<Policy[] | undefined>(
        [UserKeys.Policies],
        (oldData) => {
          if (!oldData) return;
          return [
            ...oldData.filter((policy) => policy.id !== data.id),
            newPolicy,
          ];
        }
      );
      bypass();
      navigate(`/${pagesConfig.policies.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating this policy. Please refresh and try again.',
      });
    },
    onSettled: (_returnData, _error, data) => {
      createEvent(analytics.automation.policies.update_policy, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.policies,
        policyType: data.policyType,
        manualRun: data.manualRun,
        targetType: data.sourceType,
        scheduled: data.scheduled,
        actions: data.actions,
        runImmediately: data.runImmediately,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: mutateCopyPolicy, isPending: isCopyingPolicy } = useMutation({
    mutationFn: (data: Partial<Policy>) =>
      clonePolicy(data, selectedPolicy!.policyName, changeLogMessage),
    onSuccess: (returnData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Policy copied successfully!',
      });

      const newPolicy = {
        ...(data as Policy),
        id: returnData,
      };

      queryClient.setQueryData<Policy[] | undefined>(
        [UserKeys.Policies],
        (oldData) => {
          if (!oldData) return;
          return [...oldData, newPolicy];
        }
      );
      bypass();
      navigate(`/${pagesConfig.policies.link}`);
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue copying this policy. Please refresh and try again.',
      });
    },
    onSettled: (_returnData, _error, data) => {
      createEvent(analytics.automation.policies.clone_policy, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.policies,
        policyType: data.policyType,
        manualRun: data.manualRun,
        targetType: data.sourceType,
        scheduled: data.scheduled,
        actions: data.actions,
        runImmediately: data.runImmediately,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    bypass,
    isCancelAction,
  } = useUnsavedChanges(isDirty);

  function handleFormData(data: PolicyForm) {
    const actionHash: {
      [key: string]: PolicyActionModel;
    } = {};

    data.actionData.forEach((action) => {
      if (
        action.type !== 'Create_Gmail_Filter' &&
        action.type !== 'Set_Profile_Information'
      ) {
        const { type, ...actionData } = action;
        actionHash[type] = {
          action: type,
          actionData: actionData,
          hasRun: false,
          id: 0,
        };
      } else if (action.type === 'Create_Gmail_Filter') {
        const { type, gmailFilter } = action;
        actionHash[type] = {
          action: type,
          actionData: {
            filter: '',
          },
          gmailFilter: {
            ...gmailFilter!,
          },
          hasRun: false,
          id: 0,
        };
      } else if (action.type === 'Set_Profile_Information') {
        const { type, userModelTemplate } = action;

        userModelTemplate?.ims?.map((im) => {
          if (im.type !== 'custom' && im.customType !== '') {
            delete im.customType;
          }
          if (!im.protocol) {
            delete im.protocol;
          }
        });
        if (userModelTemplate?.gender?.type === '') {
          delete userModelTemplate.gender;
        }
        userModelTemplate?.externalIds?.map((id) => {
          if (id.type !== 'custom' && id.customType !== '') {
            delete id.customType;
          }
        });

        userModelTemplate?.phones?.map((phone) => {
          if (phone.type !== 'custom' && phone.customType !== '') {
            delete phone.customType;
          }
        });

        userModelTemplate?.relations?.map((rel) => {
          if (rel.type !== 'custom' && rel.customType !== '') {
            delete rel.customType;
          }
        });
        if (
          userModelTemplate?.websites &&
          userModelTemplate?.websites.length > 0
        ) {
          userModelTemplate?.websites.map((website) => {
            if (website.type !== 'custom' && website.customType !== '') {
              delete website.customType;
            }
          });
        }

        if (userModelTemplate?.emails && userModelTemplate?.emails.length > 0) {
          userModelTemplate?.emails.map((email) => {
            if (email.type !== 'custom' && email.customType !== '') {
              delete email.customType;
            }
          });
        }

        if (
          userModelTemplate?.addresses &&
          userModelTemplate?.addresses.length > 0
        ) {
          userModelTemplate?.addresses.map((address) => {
            if (address.type !== 'custom' && address.customType !== '') {
              delete address.customType;
            }
          });
        }

        actionHash[type] = {
          action: type,
          actionData: {
            filter: '',
          },
          userModelTemplate: {
            ...userModelTemplate!,
          },
          hasRun: false,
          id: 0,
        };
      }
    });

    setValue('actionHash', actionHash);
    /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
    const { actionData, ...newPolicyWithoutActionData } = data;
    const newPolicy = {
      ...newPolicyWithoutActionData,
      actionHash,
    };

    return newPolicy;
  }

  const onSubmit: SubmitHandler<PolicyFormValues> = (data) => {
    if (dialogType === 'create' || dialogType === 'clone') {
      data.createdDate = new Date().getTime();
    }
    const newPolicy = handleFormData(data);

    if (dialogType === 'create') {
      if (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.CreatePolicy.value)) {
        setOpenChangeLogDialog(true);
      } else {
        mutateCreatePolicy(newPolicy);
      }
    } else if (dialogType === 'edit') {
      if (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.EditPolicy.value)) {
        setOpenChangeLogDialog(true);
      } else {
        mutateEditPolicy(newPolicy);
      }
    } else if (dialogType === 'clone') {
      if (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.CopyPolicy.value)) {
        setOpenChangeLogDialog(true);
      } else {
        mutateCopyPolicy(newPolicy);
      }
    }
  };

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    errors,
    trigger,
    stepCompleted,
    setStepCompleted,
  };

  const isLoading = isPolicyLoading || isDomainLoading;
  const selectedPolicyValue = getValues() as Policy;

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.automation.name },
        {
          label: pagesConfig.policies.name,
          path: pagesConfig.policies.link,
        },
      ]}
    >
      <SimpleFormPage
        title={
          dialogType === 'edit'
            ? pagesConfig.editPolicy.name
            : dialogType === 'clone'
            ? pagesConfig.clonePolicy.name
            : pagesConfig.createPolicy.name
        }
        {...formStepperProps}
        stepper={
          <SharedFormStepper {...formStepperProps} subStep={actionSubSteps} />
        }
        content={
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {(isLoading || !steps[activeStep]) && <FormSkeleton />}
            {!isLoading && steps[activeStep].label === 'Basic Information' && (
              <StepOne
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Actions' && (
              <StepTwo
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
                domains={domains || []}
                trigger={trigger}
                gPanelRoles={gPanelRoles || []}
                clearErrors={clearErrors}
                expandedIds={expandedIds}
                setExpandedIds={setExpandedIds}
                getValues={getValues}
                formState={formState}
                directoryRoles={directoryRoles}
                setActionSubSteps={setActionSubSteps}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Targets' && (
              <StepFour
                control={control}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                errors={errors}
                domains={domains || []}
                trigger={trigger}
                clearErrors={clearErrors}
              />
            )}
            {!isLoading && steps[activeStep].label === 'Schedule' && (
              <SharedScheduleStep
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                control={control}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                watch={watch}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                setValue={setValue}
                errors={errors}
                processName="policy"
              />
            )}
            {!isLoading && steps[activeStep].label === 'Review' && (
              <StepSeven selectedPolicy={selectedPolicyValue} />
            )}
          </Box>
        }
        handleSubmit={handleSubmit(onSubmit)}
        handleDiscard={handleCancel}
        firstIdWithError={firstIdWithError ?? undefined}
        setExpandedIds={setExpandedIds}
        isLoadingSubmit={
          isValidating || isSubmitting || isCreatingPolicy || isEditingPolicy
        }
        isDisableSubmit={isValidating || isSubmitting}
        submitButtonText={`${
          dialogType === 'edit'
            ? 'Update'
            : dialogType === 'clone'
            ? 'Clone'
            : 'Create'
        } Policy`}
      />

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          const newPolicy = handleFormData(getValues());
          if (dialogType === 'create') {
            mutateCreatePolicy(newPolicy);
          } else if (dialogType === 'edit') {
            mutateEditPolicy(newPolicy);
          } else if (dialogType === 'clone') {
            mutateCopyPolicy(newPolicy);
          }
        }}
        mutateLoading={isCreatingPolicy || isEditingPolicy || isCopyingPolicy}
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        entityName="Policy"
        pageMode={dialogType}
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />
    </SimpleBreadcrumbsPage>
  );
}
